import { useActiveVkuiLocation, useRouteNavigator } from '@vkontakte/vk-mini-apps-router';
import { Epic, Panel, SplitCol, SplitLayout, Tabbar, TabbarItem, View } from '@vkontakte/vkui';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { api, errorMessage } from '../api/client';
import { useLoader } from '../api/loader';
import { isStaff, resetSession, SessionContext, startSession, type Session } from '../api/session';
import type { BadgeCounters, MyAccountResponse, QueueCounters } from '../api/types';
import {
  AbilitiesPanel,
  AbilityReviewPanel,
  AdminPanel,
  RegistryPanel,
  CharacterAbilitiesPanel,
  CharacterCardPanel,
  CharacterItemsPanel,
  CharacterPetsPanel,
  DataDeletionPanel,
  DecisionsPanel,
  FaceClaimPanel,
  FaceClaimsPanel,
  FormDiffPanel,
  FormHistoryPanel,
  FormEditorPanel,
  FormReviewPanel,
  GuestOnboardingPanel,
  InventoryPanel,
  ItemReviewPanel,
  PetReviewPanel,
  MorePanel,
  MyCharacterPanel,
  PetsPanel,
  ProfilePanel,
  RulesPanel,
  TaskQueuePanel,
} from '../features';
import { t } from '../i18n';
import {
  TabRegistryIcon,
  TabCharacterIcon,
  TabFacesIcon,
  TabMoreIcon,
  TabTasksIcon,
} from '../shared/icons';
import {
  ErrorState,
  LoadingState,
  PhotoViewer,
  ScreenHeader,
  ScreenTitle,
  TABBAR_ITEM_ACTIVE,
  TabCount,
  ToastHost,
} from '../shared/ui';
import { useCountersChanged } from './counters';
import { leaveGuarded } from './leaveGuard';
import { PANEL, VIEW, VIEW_ROOT_PANEL, VIEW_ROOT_PATH, type PanelId, type ViewId } from './router';

const TAB_LABELS: Record<ViewId, string> = {
  [VIEW.REGISTRY]: t('app.tab.registry'),
  [VIEW.FACES]: t('app.tab.faces'),
  [VIEW.CHARACTER]: t('app.tab.character'),
  [VIEW.TASKS]: t('app.tab.tasks'),
  [VIEW.MORE]: t('app.tab.more'),
};

// Значки свои: вкуишные Icon28* сплошные и в своей палитре, рядом с
// контурной навигацией Мезы они читались как чужая панель.
const TAB_ICONS: Record<ViewId, ReactElement> = {
  [VIEW.REGISTRY]: <TabRegistryIcon />,
  [VIEW.FACES]: <TabFacesIcon />,
  [VIEW.CHARACTER]: <TabCharacterIcon />,
  [VIEW.TASKS]: <TabTasksIcon />,
  [VIEW.MORE]: <TabMoreIcon />,
};

const GUEST_TABS: readonly ViewId[] = [VIEW.REGISTRY, VIEW.FACES, VIEW.CHARACTER, VIEW.MORE];
const STAFF_TABS: readonly ViewId[] = [
  VIEW.REGISTRY,
  VIEW.FACES,
  VIEW.CHARACTER,
  VIEW.TASKS,
  VIEW.MORE,
];

type Boot =
  | { kind: 'loading' }
  | { kind: 'failed'; message: string }
  | {
      kind: 'ready';
      session: Session;
      tabs: readonly ViewId[];
    };

export function App() {
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<{ attempt: number; boot: Boot } | null>(null);

  const { view, panel } = useActiveVkuiLocation();
  const currentView = (view ?? VIEW.REGISTRY) as ViewId;
  const currentPanel = (panel ?? VIEW_ROOT_PANEL[currentView]) as PanelId;

  useEffect(() => {
    let alive = true;

    void (async () => {
      try {
        const session = await startSession();
        if (!alive) return;
        // Состав таббара считаем один раз, по ответу /me. Если роль сменят
        // прямо сейчас, вкладки не перестроятся — это осознанно: пункты не
        // должны появляться и пропадать под пальцем. Перезапуск приложения
        // всё подхватит.
        setSettled({
          attempt,
          boot: { kind: 'ready', session, tabs: isStaff(session) ? STAFF_TABS : GUEST_TABS },
        });
      } catch (error: unknown) {
        if (alive) setSettled({ attempt, boot: { kind: 'failed', message: errorMessage(error) } });
      }
    })();

    return () => {
      alive = false;
    };
  }, [attempt]);

  // Ответ не на эту попытку — значит, сессия ещё поднимается.
  const boot: Boot = settled?.attempt === attempt ? settled.boot : { kind: 'loading' };

  const badges = useBadges(boot.kind === 'ready' ? boot.session : null, currentView);

  /**
   * Перезапуск сессии на месте. Нужен трижды: после сетевой ошибки на старте,
   * после самостоятельной записи в список участников и после удаления своих
   * данных — роль, вкладки и состав экранов приложение считает один раз, по
   * ответу `/me`.
   */
  const restart = useCallback(() => {
    resetSession();
    setAttempt((value) => value + 1);
  }, []);

  if (boot.kind === 'loading') {
    return (
      <BootScreen>
        <LoadingState text={t('app.boot.loading')} />
      </BootScreen>
    );
  }

  if (boot.kind === 'failed') {
    return (
      <BootScreen>
        <ErrorState title={t('app.boot.error')} message={boot.message} onRetry={restart} />
      </BootScreen>
    );
  }

  return (
    <SessionContext.Provider value={boot.session}>
      <MainLayout
        session={boot.session}
        tabs={boot.tabs}
        badges={badges}
        currentView={currentView}
        currentPanel={currentPanel}
        onRestart={restart}
      />
    </SessionContext.Provider>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Каркас
   ────────────────────────────────────────────────────────────────────────── */

/**
 * Каркас: одна колонка. Второй не бывает — боковых панелей у нас нет.
 *
 * Ширину НЕ ограничиваем: приложение адаптивное, и VKUI сам подгоняет вёрстку
 * под ширину окна. Жёсткий maxWidth зажимал десктоп в колонку шириной с
 * телефон, а таббар при этом всё равно тянулся во всю страницу — он у VKUI
 * прибит position: fixed.
 */
function Shell({ children }: { children: ReactNode }) {
  return (
    <SplitLayout>
      <SplitCol>{children}</SplitCol>
    </SplitLayout>
  );
}

/** Экран до входа: вкладок ещё нет, поэтому и Epic здесь нет — одна панель. */
function BootScreen({ children }: { children: ReactNode }) {
  return (
    <Shell>
      <View id="boot" activePanel="boot">
        <Panel id="boot" disableBackground>
          <ScreenHeader>
            <ScreenTitle>{t('app.title')}</ScreenTitle>
          </ScreenHeader>
          {children}
        </Panel>
      </View>
    </Shell>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Вкладки и их экраны
   ────────────────────────────────────────────────────────────────────────── */

interface MainLayoutProps {
  session: Session;
  tabs: readonly ViewId[];
  badges: BadgeCounters | null;
  currentView: ViewId;
  currentPanel: PanelId;
  onRestart: () => void;
}

function MainLayout({
  session,
  tabs,
  badges,
  currentView,
  currentPanel,
  onRestart,
}: MainLayoutProps) {
  const routeNavigator = useRouteNavigator();

  // Гость в реестре не заперт: вкладку «Персонаж» он видит, но внутри —
  // объяснение, а не редактор.
  const hasCharacterAccess = session.user !== null && session.user.canWrite;

  // Вкладку, которой нет в таббаре, показывать нельзя даже по прямой ссылке.
  const activeView = tabs.includes(currentView) ? currentView : VIEW.REGISTRY;

  const panelFor = (target: ViewId): PanelId =>
    activeView === target ? currentPanel : VIEW_ROOT_PANEL[target];

  const tabbar = useMemo(
    () => (
      <Tabbar>
        {tabs.map((tab) => (
          <TabbarItem
            key={tab}
            // Светящаяся черта над выбранной вкладкой: в макетах это главный
            // признак «вы здесь», подсветка подписи там вторична. Класс свой,
            // чертит его ui.module.css — внутренности TabbarItem не трогаем.
            className={activeView === tab ? TABBAR_ITEM_ACTIVE : undefined}
            selected={activeView === tab}
            // Смена вкладки — тоже уход с экрана: из редактора анкеты
            // сторож успеет спросить, сохранять ли набранное.
            onClick={() => leaveGuarded(() => routeNavigator.push(VIEW_ROOT_PATH[tab]))}
            label={TAB_LABELS[tab]}
            indicator={indicatorFor(tab, badges)}
          >
            {TAB_ICONS[tab]}
          </TabbarItem>
        ))}
      </Tabbar>
    ),
    [tabs, activeView, badges, routeNavigator],
  );

  // Гостю вкладка «Персонаж» отдаёт одну панель: редактор и списки ему
  // открывать нечем, и по прямой ссылке их тоже не будет.
  const characterPanels = hasCharacterAccess
    ? [
        <MyCharacterPanel key={PANEL.CHARACTER_HUB} id={PANEL.CHARACTER_HUB} />,
        <FormEditorPanel key={PANEL.CHARACTER_FORM} id={PANEL.CHARACTER_FORM} />,
        <FormDiffPanel key={PANEL.CHARACTER_FORM_DIFF} id={PANEL.CHARACTER_FORM_DIFF} />,
        <FormHistoryPanel key={PANEL.CHARACTER_FORM_HISTORY} id={PANEL.CHARACTER_FORM_HISTORY} />,
        <InventoryPanel key={PANEL.CHARACTER_INVENTORY} id={PANEL.CHARACTER_INVENTORY} />,
        <PetsPanel key={PANEL.CHARACTER_PETS} id={PANEL.CHARACTER_PETS} />,
        <AbilitiesPanel key={PANEL.CHARACTER_ABILITIES} id={PANEL.CHARACTER_ABILITIES} />,
        <DecisionsPanel key={PANEL.CHARACTER_DECISIONS} id={PANEL.CHARACTER_DECISIONS} />,
      ]
    : [
        <GuestOnboardingPanel
          key={PANEL.CHARACTER_HUB}
          id={PANEL.CHARACTER_HUB}
          onJoined={onRestart}
        />,
      ];

  // Экраны вкладки. Ключ — та же вкладка, что в таббаре, поэтому состав Epic
  // считается по одному списку tabs: своего условия про «Задачи» здесь больше
  // нет, и разойтись таббар с экранами не может.
  const stories: Record<ViewId, ReactElement> = {
    [VIEW.REGISTRY]: (
      <View key={VIEW.REGISTRY} id={VIEW.REGISTRY} activePanel={panelFor(VIEW.REGISTRY)}>
        <RegistryPanel id={PANEL.REGISTRY_LIST} />
        <CharacterCardPanel id={PANEL.REGISTRY_CHARACTER} />
        <CharacterItemsPanel id={PANEL.REGISTRY_ITEMS} />
        <CharacterPetsPanel id={PANEL.REGISTRY_PETS} />
        <CharacterAbilitiesPanel id={PANEL.REGISTRY_ABILITIES} />
      </View>
    ),

    [VIEW.FACES]: (
      <View key={VIEW.FACES} id={VIEW.FACES} activePanel={panelFor(VIEW.FACES)}>
        <FaceClaimsPanel id={PANEL.FACES_LIST} />
        <FaceClaimPanel id={PANEL.FACES_ITEM} />
      </View>
    ),

    [VIEW.CHARACTER]: (
      <View
        key={VIEW.CHARACTER}
        id={VIEW.CHARACTER}
        activePanel={hasCharacterAccess ? panelFor(VIEW.CHARACTER) : PANEL.CHARACTER_HUB}
      >
        {characterPanels}
      </View>
    ),

    [VIEW.TASKS]: (
      <View key={VIEW.TASKS} id={VIEW.TASKS} activePanel={panelFor(VIEW.TASKS)}>
        <TaskQueuePanel id={PANEL.TASKS_QUEUE} />
        <FormReviewPanel id={PANEL.TASKS_FORM_REVIEW} />
        <ItemReviewPanel id={PANEL.TASKS_ITEM_REVIEW} />
        <PetReviewPanel id={PANEL.TASKS_PET_REVIEW} />
        <AbilityReviewPanel id={PANEL.TASKS_ABILITY_REVIEW} />
      </View>
    ),

    [VIEW.MORE]: (
      <View key={VIEW.MORE} id={VIEW.MORE} activePanel={panelFor(VIEW.MORE)}>
        <MorePanel id={PANEL.MORE_MENU} />
        <ProfilePanel id={PANEL.MORE_PROFILE} />
        <AdminPanel id={PANEL.MORE_ADMIN} />
        <RulesPanel id={PANEL.MORE_RULES} />
        <DataDeletionPanel id={PANEL.MORE_DATA_DELETION} onDeleted={onRestart} />
      </View>
    ),
  };

  return (
    <Shell>
      <Epic activeStory={activeView} tabbar={tabbar}>
        {tabs.map((tab) => stories[tab])}
      </Epic>
      <PhotoViewer />
      <ToastHost />
    </Shell>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
   Цифры на вкладках
   ────────────────────────────────────────────────────────────────────────── */

/**
 * Счётчики перечитываем при смене вкладки и по `countersChanged()`: прочтение
 * решений и разбор заявки меняют цифру, не уводя с вкладки. Сделанное другими
 * людьми приезжает только со сменой вкладки — пуша нет.
 *
 * Отдельного маршрута с бейджами на сервере нет — цифры собираем из двух
 * уже существующих ответов: размер очередей модерации и настройки аккаунта,
 * где лежит число непрочитанных решений.
 */
function useBadges(session: Session | null, currentView: ViewId): BadgeCounters | null {
  // Оба запроса ниже закрыты правами, и спрашивать их наугад нельзя: чужой
  // роли сервер ответит 403, а не пустыми цифрами.
  const sessionUserId = session?.user?.id ?? null;
  const staffSession = session !== null && isStaff(session);
  const writableSession = session?.user?.canWrite ?? false;

  const badges = useLoader<BadgeCounters>(
    async (signal) => {
      // Бейджи — украшение: любой из двух запросов может не ответить, и это не
      // повод ронять вкладки. Не ответил — рисуем их без цифры.
      const [queue, account] = await Promise.all([
        staffSession
          ? api.get<QueueCounters>('/api/review/queues', undefined, signal).catch(() => null)
          : null,
        // Счётчик непрочитанных решений ключуется по аккаунту, а не по
        // персонажу, поэтому его отдаёт users, а не кабинет. Запрос всё равно
        // под правом записи: гостю сервер ответит 403, а не пустыми цифрами.
        writableSession
          ? api.get<MyAccountResponse>('/api/users/me', undefined, signal).catch(() => null)
          : null,
      ]);
      return { unreadDecisions: account?.unreadDecisions ?? 0, queue };
    },
    [sessionUserId, currentView, staffSession, writableSession],
    sessionUserId !== null,
  );

  useCountersChanged(badges.reload);

  return badges.data;
}

/**
 * Цифра на вкладке: решения по моим заявкам и размер очереди модерации.
 *
 * Малиновая, а не бирюзовая: бирюза в приложении означает «принято и живо», и
 * ею же подсвечена сама активная вкладка — счётчик тонул в ней. В макетах эта
 * цифра единственная залитая малиновым во всём таббаре.
 */
function indicatorFor(tab: ViewId, badges: BadgeCounters | null): ReactElement | undefined {
  if (!badges) return undefined;

  if (tab === VIEW.CHARACTER && badges.unreadDecisions > 0) {
    return <TabCount>{badges.unreadDecisions}</TabCount>;
  }

  if (tab === VIEW.TASKS && badges.queue !== null && badges.queue.total > 0) {
    return <TabCount>{badges.queue.total}</TabCount>;
  }

  return undefined;
}
